import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Work, WorkFilters, WorksCatalog, WorkState } from "../../types/works";
import { worksAPI } from "../../services/api";

// ─── Dispatcher thunks ──────────────────────────────────────────────────────────

export const getWorks = createAsyncThunk(
  "works/getWorks",
  async (filters: WorkFilters | undefined, { rejectWithValue }) => {
    try {
      const res = await worksAPI.getWorks(filters as Record<string, unknown>);
      return res.data.data as {
        works: Work[];
        pagination: WorkState["pagination"];
        stats: WorkState["stats"];
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to fetch works");
    }
  },
);

export const getWork = createAsyncThunk(
  "works/getWork",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await worksAPI.getWork(id);
      return res.data.data.work as Work;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Work not found");
    }
  },
);

export const createWork = createAsyncThunk(
  "works/createWork",
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const res = await worksAPI.createWork(data);
      return res.data.data.work as Work;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to create work");
    }
  },
);

export const updateWork = createAsyncThunk(
  "works/updateWork",
  async ({ id, data }: { id: string; data: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      const res = await worksAPI.updateWork(id, data);
      return res.data.data.work as Work;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to update work");
    }
  },
);

export const deleteWork = createAsyncThunk(
  "works/deleteWork",
  async (id: string, { rejectWithValue }) => {
    try {
      await worksAPI.deleteWork(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to delete work");
    }
  },
);

export const getCatalog = createAsyncThunk(
  "works/getCatalog",
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await worksAPI.getCatalog();
      return res.data.data as WorksCatalog;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to fetch catalog");
    }
  },
);

// ─── Driver thunks ──────────────────────────────────────────────────────────────

export const getDriverWorks = createAsyncThunk(
  "works/getDriverWorks",
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await worksAPI.getDriverWorks();
      return res.data.data.works as Work[];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Failed to fetch works");
    }
  },
);

export const getDriverWork = createAsyncThunk(
  "works/getDriverWork",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await worksAPI.getDriverWork(id);
      return res.data.data.work as Work;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Work not found");
    }
  },
);

/**
 * Silent background refresh of the open driver work — used by polling. Unlike
 * getDriverWork it never flips `loading` or clears `currentWork` on pending, so
 * a periodic poll updates the page in place without flashing the spinner.
 */
export const refreshDriverWork = createAsyncThunk(
  "works/refreshDriverWork",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await worksAPI.getDriverWork(id);
      return res.data.data.work as Work;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Work not found");
    }
  },
);

/** Factory for driver execution thunks that all resolve to the updated work. */
const execThunk = <Arg>(
  name: string,
  call: (arg: Arg) => Promise<{ data: { data: { work: Work } } }>,
) =>
  createAsyncThunk(`works/${name}`, async (arg: Arg, { rejectWithValue }) => {
    try {
      const res = await call(arg);
      return res.data.data.work as Work;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? "Action failed");
    }
  });

type ExecArg = { id: string; data?: Record<string, unknown> };

export const startWork = execThunk<ExecArg>("startWork", ({ id, data }) => worksAPI.startWork(id, data));
export const pauseWork = execThunk<ExecArg>("pauseWork", ({ id, data }) => worksAPI.pauseWork(id, data));
export const resumeWork = execThunk<ExecArg>("resumeWork", ({ id, data }) => worksAPI.resumeWork(id, data));
export const completeWork = execThunk<ExecArg>("completeWork", ({ id, data }) => worksAPI.completeWork(id, data));
export const roundLoaded = execThunk<ExecArg>("roundLoaded", ({ id, data }) => worksAPI.roundLoaded(id, data));
export const roundUnloaded = execThunk<ExecArg>("roundUnloaded", ({ id, data }) => worksAPI.roundUnloaded(id, data));
export const recordStep = execThunk<{ id: string; data: Record<string, unknown> }>(
  "recordStep",
  ({ id, data }) => worksAPI.step(id, data),
);
export const updateMetric = execThunk<{ id: string; data: Record<string, unknown> }>(
  "updateMetric",
  ({ id, data }) => worksAPI.updateMetric(id, data),
);
export const addService = execThunk<{ id: string; data: Record<string, unknown> }>(
  "addService",
  ({ id, data }) => worksAPI.addService(id, data),
);
export const updateServiceQuantity = execThunk<{
  id: string;
  lineId: string;
  data: Record<string, unknown>;
}>("updateServiceQuantity", ({ id, lineId, data }) =>
  worksAPI.updateServiceQuantity(id, lineId, data),
);

const workReturningThunks = [
  startWork,
  pauseWork,
  resumeWork,
  completeWork,
  roundLoaded,
  roundUnloaded,
  recordStep,
  updateMetric,
  addService,
  updateServiceQuantity,
];

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: WorkState = {
  works: [],
  currentWork: null,
  catalog: null,
  loading: false,
  error: null,
  pagination: { page: 1, limit: 20, total: 0, pages: 0 },
  stats: { total: 0, in_progress: 0, planned: 0, overdue: 0 },
};

/**
 * Repairs a work coming off the API so the UI can always read its nested shape.
 * Mongoose can omit an all-empty embedded metric subdoc, so tonnage/distanceKm/
 * hours may arrive undefined; services/events may be missing. Defaulting them
 * here (the single ingress point) protects every downstream read site.
 */
const normalizeWork = (work: Work): Work => ({
  ...work,
  tonnage: work.tonnage ?? {},
  distanceKm: work.distanceKm ?? {},
  hours: work.hours ?? {},
  services: Array.isArray(work.services) ? work.services : [],
  events: Array.isArray(work.events) ? work.events : [],
  rounds: Array.isArray(work.rounds) ? work.rounds : [],
});

const replaceWork = (state: WorkState, work: Work) => {
  const normalized = normalizeWork(work);
  const i = state.works.findIndex((w) => w._id === normalized._id);
  if (i !== -1) state.works[i] = normalized;
  if (state.currentWork?._id === normalized._id) state.currentWork = normalized;
};

const worksSlice = createSlice({
  name: "works",
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearCurrentWork: (state) => { state.currentWork = null; },
    setCurrentWork: (state, action: PayloadAction<Work | null>) => {
      state.currentWork = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getWorks.fulfilled, (state, action) => {
        state.loading = false;
        state.works = action.payload.works.map(normalizeWork);
        state.pagination = action.payload.pagination;
        if (action.payload.stats) state.stats = action.payload.stats;
      })
      .addCase(getWorks.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch works";
      })
      .addCase(getWork.fulfilled, (state, action) => { state.currentWork = normalizeWork(action.payload); })
      .addCase(createWork.fulfilled, (state, action) => {
        state.works.unshift(normalizeWork(action.payload));
        state.pagination.total += 1;
      })
      .addCase(updateWork.fulfilled, (state, action) => { replaceWork(state, action.payload); })
      .addCase(deleteWork.fulfilled, (state, action) => {
        state.works = state.works.filter((w) => w._id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      })
      .addCase(getCatalog.fulfilled, (state, action) => { state.catalog = action.payload; })
      // Driver
      .addCase(getDriverWorks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getDriverWorks.fulfilled, (state, action) => {
        state.loading = false;
        state.works = action.payload.map(normalizeWork);
      })
      .addCase(getDriverWorks.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch works";
      })
      // Clear the stale currentWork on pending so the execution page doesn't
      // flash the previous work (or a "not found" message) before the fetch
      // for the requested id resolves.
      .addCase(getDriverWork.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentWork = null;
      })
      .addCase(getDriverWork.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWork = normalizeWork(action.payload);
      })
      .addCase(getDriverWork.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to fetch work";
      })
      // Silent poll — only update the work in place if it is still the open one.
      .addCase(refreshDriverWork.fulfilled, (state, action) => {
        const fresh = normalizeWork(action.payload);
        if (state.currentWork?._id === fresh._id) state.currentWork = fresh;
        replaceWork(state, fresh);
      });

    // All driver execution thunks resolve to the updated work.
    workReturningThunks.forEach((thunk) => {
      builder.addCase(thunk.fulfilled, (state, action) => {
        replaceWork(state, action.payload as Work);
      });
    });
  },
});

export const { clearError, clearCurrentWork, setCurrentWork } = worksSlice.actions;
export default worksSlice.reducer;
